// src/pages/Login.js
import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import TwoFactorVerification from '../components/auth/TwoFactorVerification';
import MandatoryTwoFactorSetup from '../components/auth/MandatoryTwoFactorSetup';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link as MuiLink,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Lock, 
  Login as LoginIcon,
  Shield,
  Security,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';

export default function Login() {
  const [view, setView] = useState('login'); // 'login', 'forgotPassword', 'resetSent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, user } = useContext(AuthContext);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(location.state?.message || null);
  const [needsMFA, setNeedsMFA] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  // Check for password reset success message
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('passwordReset') === 'success') {
      setSuccess('Wachtwoord succesvol gewijzigd. U kunt nu inloggen met uw nieuwe wachtwoord.');
      // Clean up URL
      navigate('/login', { replace: true });
    }
  }, [location.search, navigate]);

  // Unified auth state check - runs when user changes
  useEffect(() => {
    // Skip if no user or already showing MFA/setup screens
    if (!user || needsMFA || needsSetup) {
      return;
    }

    let isMounted = true;

    const checkAuthState = async () => {
      try {
        // 0. Check if this is a recovery session - if so, redirect to reset password
        await supabase.auth.getSession();
        
        // Check if URL contains recovery hash or if session metadata indicates recovery
        const urlHash = window.location.hash;
        const isRecoveryUrl = urlHash.includes('type=recovery');
        
        if (isRecoveryUrl) {
          if (isMounted) {
            navigate('/reset-password', { replace: true });
          }
          return;
        }
        
        // 1. Check if user has verified MFA factors
        const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
        
        if (factorsError) {
          console.error('Error checking factors:', factorsError);
          // On error, just redirect - don't block user
          if (isMounted) {
            const from = location.state?.from?.pathname || "/";
            navigate(from === '/login' ? '/' : from, { replace: true });
          }
          return;
        }

        const allFactors = [...factorsData.totp, ...factorsData.phone];
        const hasVerifiedFactor = allFactors.some(factor => factor.status === 'verified');

        // 2. If no MFA setup, force setup
        if (!hasVerifiedFactor) {
          if (isMounted) setNeedsSetup(true);
          return;
        }

        // 3. Check AAL level
        const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        
        // 4. Determine action based on state
        if (!isMounted) return;

        if (aalData?.currentLevel === 'aal2') {
          // Fully authenticated, redirect to app
          const from = location.state?.from?.pathname || "/";
          navigate(from === '/login' ? '/' : from, { replace: true });
        } else if (aalData?.nextLevel === 'aal2' && aalData?.currentLevel === 'aal1') {
          // Need MFA verification
          setNeedsMFA(true);
        } else {
          // Fallback: just redirect
          const from = location.state?.from?.pathname || "/";
          navigate(from === '/login' ? '/' : from, { replace: true });
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
        // On error, redirect anyway
        if (isMounted) {
          const from = location.state?.from?.pathname || "/";
          navigate(from === '/login' ? '/' : from, { replace: true });
        }
      }
    };

    checkAuthState();

    return () => {
      isMounted = false;
    };
  }, [user, needsMFA, needsSetup, navigate, location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    
    try {
      // Attempt login
      const { error: loginError } = await login({ email, password });
      
      if (loginError) {
        setError(loginError.message);
        return;
      }

      // Login successful! The useEffect will handle the rest (MFA check, redirect, etc.)
      // No need to duplicate auth state logic here
      
    } catch (error) {
      console.error('Login error:', error);
      setError('Er is een fout opgetreden bij het inloggen');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (resetError) {
      setError(resetError.message);
    } else {
      setView('resetSent');
    }
  };

  const handle2FASuccess = () => {
    // 2FA verificatie succesvol, navigeer naar gewenste pagina
    const from = location.state?.from?.pathname || "/";
    navigate(from === '/login' ? '/' : from, { replace: true });
  };

  const handle2FABack = () => {
    // Terug naar normale login
    setNeedsMFA(false);
  };

  const handleMandatorySetupComplete = () => {
    // Verplichte 2FA setup voltooid, navigeer naar app
    const from = location.state?.from?.pathname || "/";
    navigate(from === '/login' ? '/' : from, { replace: true });
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    tap: {
      scale: 0.98
    }
  };

  const renderContent = () => {
    switch (view) {
      case 'forceSetup':
        return (
          <MandatoryTwoFactorSetup 
            user={{ email }}
            onComplete={handleMandatorySetupComplete}
          />
        );
      case 'mfa':
        return (
          <TwoFactorVerification 
            onSuccess={handle2FASuccess}
            onBack={handle2FABack}
          />
        );
      case 'forgotPassword':
        return (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box component="form" onSubmit={handleForgotPassword} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Security sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Wachtwoord herstellen
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
                Geen zorgen! Voer je e-mailadres in en we sturen je een beveiligde link om je wachtwoord opnieuw in te stellen.
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                label="E-mail"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
                    }
                  }
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }
                }}
              />
              <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={loading}
                  sx={{ 
                    mt: 3, 
                    mb: 2, 
                    py: 1.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Herstellink versturen"}
                </Button>
              </motion.div>
              <MuiLink 
                component="button" 
                variant="body2" 
                onClick={() => setView('login')} 
                sx={{ 
                  display: 'block', 
                  textAlign: 'center', 
                  color: 'primary.main',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                ← Terug naar inloggen
              </MuiLink>
            </Box>
          </motion.div>
        );
      case 'resetSent':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ mb: 3 }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <Shield sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                </motion.div>
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                Check je e-mail
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.6 }}>
                We hebben een beveiligde herstellink gestuurd naar<br />
                <strong style={{ color: theme.palette.primary.main }}>{email}</strong>
              </Typography>
              <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
                <Button
                  variant="contained"
                  onClick={() => setView('login')}
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  Terug naar inloggen
                </Button>
              </motion.div>
            </Box>
          </motion.div>
        );
      default: // 'login'
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="E-mail"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
                    }
                  }
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Wachtwoord"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
                    }
                  }
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: 'text.secondary' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                }}
              />
              <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? null : <LoginIcon />}
                  sx={{ 
                    mt: 3, 
                    mb: 2, 
                    py: 1.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Veilig Inloggen"}
                </Button>
              </motion.div>
              <MuiLink 
                component="button" 
                variant="body2" 
                onClick={() => setView('forgotPassword')} 
                sx={{ 
                  display: 'block', 
                  textAlign: 'center', 
                  color: 'primary.main',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Wachtwoord vergeten?
              </MuiLink>
            </Box>
          </motion.div>
        );
    }
  };

  // Als 2FA verificatie nodig is
  if (needsMFA) {
    return <TwoFactorVerification onSuccess={handle2FASuccess} onBack={handle2FABack} />;
  }

  // Als verplichte 2FA setup nodig is
  if (needsSetup) {
    return <MandatoryTwoFactorSetup user={{ email }} onComplete={handleMandatorySetupComplete} />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.light}20 50%, ${theme.palette.primary.main}10 100%)`,
        p: 2,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23000000" fill-opacity="0.02"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        }
      }}
    >
      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={cardVariants}>
            <Paper 
              elevation={8} 
              sx={{ 
                p: { xs: 3, sm: 4 }, 
                width: '100%', 
                borderRadius: 4,
                background: (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(0, 0, 0, 0.6)' 
                  : 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(20px)',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) => `0 20px 40px -12px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.15)'}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <motion.div
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      mb: 2,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 8px 20px ${theme.palette.primary.main}40`,
                      p: 1.5,
                    }}
                  >
                    <Box
                      component="img"
                      src="/logo192.png"
                      alt="BEM.report Logo"
                      sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: 2,
                        filter: 'brightness(0) invert(1)',
                      }}
                    />
                  </Box>
                </motion.div>
                <Typography variant="h4" component="h1" sx={{ 
                  fontWeight: 'bold',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5
                }}>
                  BEM.report
                </Typography>
                {view !== 'resetSent' && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mt: 0.5,
                      fontWeight: 500,
                      textAlign: 'center'
                    }}>
                    {view === 'login' ? 'Beveiligde toegang tot jouw projecten' : ''}
                  </Typography>
                )}
              </Box>

              {success && view === 'login' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mb: 3, 
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        color: 'success.main'
                      }
                    }} 
                    onClose={() => setSuccess(null)}
                  >
                    {success}
                  </Alert>
                </motion.div>
              )}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3, 
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        color: 'error.main'
                      }
                    }} 
                    onClose={() => setError(null)}
                  >
                    {error}
                  </Alert>
                </motion.div>
              )}

              {renderContent()}
            </Paper>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
}